import type { Messages } from "@/i18n/locales/en";
import { legalMessagesIt } from "@/i18n/locales/legalItData";
import { pagesMessagesIt } from "@/i18n/locales/pagesItData";

export const itMessages = {
  meta: {
    description:
      "WasOK — scansiona prodotti, salva valutazioni e note, ricorda cosa valeva la pena comprare.",
    title: "WasOK — Ricorda cosa valeva la pena comprare",
  },
  header: {
    navFeatures: "Funzionalità",
    navHowItWorks: "Come funziona",
    navPrivacy: "Privacy",
    navUseCases: "Casi d'uso",
    download: "Scarica",
    openMenu: "Apri menu",
    closeMenu: "Chiudi menu",
    languageAria: "Lingua",
  },
  hero: {
    badge: "📱 Ora disponibile su Android",
    title: "Ricorda cosa valeva la pena comprare.",
    subtitle:
      "Scansiona i prodotti, salva valutazioni e note, e ricorda subito se un prodotto era davvero buono.",
    googlePlay: "Scarica su Google Play",
    watchDemo: "Guarda la demo",
    privacyLine: "Tutti i dati restano sul tuo dispositivo.",
    phoneTitle: "WasOK",
    phoneSubtitle: "Scansiona per ricordare",
    snackCard: "Delizioso!",
    coffeeCard: "Troppo amaro",
    scrollHint: "Scorri per esplorare",
  },
  problems: {
    heading: "Ti suona familiare?",
    subheading:
      "Ci siamo passati tutti — davanti allo scaffale a cercare di ricordare se ci era piaciuto l'ultima volta.",
    notedAgo: "✓ Lo hai annotato 3 mesi fa",
    items: [
      {
        question: "Quale cioccolato mi è piaciuto davvero?",
        note: "Dolcezza perfetta",
      },
      {
        question: "Perché ho smesso di comprare questo shampoo?",
        note: "Mi ha seccato i capelli",
      },
      {
        question: "Questo caffè era buono o deludente?",
        note: "Troppo amaro",
      },
    ],
  },
  howItWorks: {
    heading: "Come funziona",
    subheading:
      "Quattro semplici passaggi per non dimenticare mai più le tue opinioni sui prodotti.",
    stepPrefix: "Passaggio",
    steps: [
      {
        title: "Scansiona qualsiasi codice a barre",
        description:
          "Scansione rapida e facile con la fotocamera. Funziona con qualsiasi codice a barre.",
      },
      {
        title: "Salva valutazioni, commenti e foto",
        description:
          "Valuta i prodotti, aggiungi note, allega foto e registra prezzi e luoghi.",
      },
      {
        title: "Ricorda subito la tua esperienza",
        description:
          "Scansiona di nuovo lo stesso prodotto e vedi la valutazione e le note precedenti.",
      },
      {
        title: "Crea liste della spesa intelligenti",
        description:
          "Crea liste della spesa dai prodotti di cui ti fidi già e che ami.",
      },
    ],
  },
  privacy: {
    heading: "La tua cronologia degli acquisti resta tua.",
    body: "WasOK ti aiuta a ricordare i prodotti che hai provato, mantenendo valutazioni, note, foto e liste della spesa archiviate localmente sul tuo dispositivo.",
    trustBanner:
      "💚 Costruito intorno alla tua cronologia degli acquisti — non intorno alla raccolta dei tuoi dati.",
    bullets: [
      "Nessun account richiesto",
      "Nessuna sincronizzazione cloud richiesta",
      "Archiviazione locale",
      "Backup sotto il tuo controllo",
      "Ricerca opzionale dei codici a barre per informazioni sui prodotti.",
      "Supportato da pubblicità con Google AdMob",
    ],
  },
  features: {
    heading: "Tutto ciò di cui hai bisogno",
    subheading:
      "Funzionalità potenti progettate per rendere il tracking dei prodotti semplice e privato.",
    items: [
      {
        title: "Scanner di codici a barre",
        description: "Scansione rapida e precisa dei codici a barre per qualsiasi prodotto.",
      },
      {
        title: "Valutazioni prodotti",
        description: "Valuta i prodotti da 1 a 5 stelle per ricordare la qualità.",
      },
      {
        title: "Foto dei prodotti",
        description: "Allega foto per ricordare la confezione o i dettagli.",
      },
      {
        title: "Categorie",
        description: "Organizza i prodotti in categorie personalizzate.",
      },
      {
        title: "Tracciamento negozi",
        description: "Ricorda dove hai acquistato ogni prodotto.",
      },
      {
        title: "Registrazione prezzi",
        description: "Monitora i prezzi per trovare le migliori offerte.",
      },
      {
        title: "Liste della spesa",
        description: "Crea liste dai tuoi prodotti preferiti.",
      },
      {
        title: "Accesso offline",
        description: "Funziona perfettamente senza connessione internet.",
      },
      {
        title: "Archiviazione locale",
        description: "Tutti i tuoi dati archiviati in modo sicuro sul tuo dispositivo.",
      },
    ],
  },
  useCases: {
    heading: "Perfetto per ogni corsia",
    subheading:
      "Che tu stia facendo la spesa per cibo, bellezza o articoli per la casa — WasOK ti aiuta a ricordare.",
    cards: [
      {
        emoji: "🍫",
        title: "Snack e dolci",
        description: "Ricorda quali caramelle erano davvero buone.",
      },
      {
        emoji: "☕",
        title: "Caffè e bevande",
        description: "Tieni traccia dei caffè che vale la pena ricomprare.",
      },
      {
        emoji: "💄",
        title: "Cosmetici e skincare",
        description: "Annota le reazioni della pelle.",
      },
      {
        emoji: "🧹",
        title: "Articoli per la casa",
        description: "Evita di ricomprare prodotti che hanno deluso.",
      },
    ],
  },
  screenshots: {
    heading: "Bello e intuitivo",
    subheading:
      "Un'interfaccia pensata con cura che rende piacevole il tracking dei prodotti.",
    items: [
      { title: "Scanner" },
      { title: "Dettaglio prodotto" },
      { title: "Inserimento valutazione" },
      { title: "Ricerca e cronologia" },
      { title: "Lista della spesa" },
    ],
  },
  cta: {
    heading: "Memoria della spesa per la vita reale.",
    body: "Smetti di dimenticare quali prodotti valeva la pena comprare. Inizia a fare shopping in modo più intelligente oggi.",
    googlePlay: "Scarica su Google Play",
    scanQr: "Scansiona per scaricare",
    tagline: "Privato · Offline · Semplice",
  },
  footer: {
    tagline: "Ricorda cosa valeva la pena comprare.",
    linksHeading: "Link",
    privacyPolicy: "Informativa sulla privacy",
    terms: "Termini di servizio",
    contact: "Contatti",
    linkFaq: "FAQ",
    linkLicenses: "Licenze open source",
    linkAdvertising: "Pubblicità e monetizzazione",
    downloadHeading: "Scarica",
    googlePlay: "Google Play",
    copyright:
      "© 2026 WasOK. Tutti i diritti riservati. Fatto con 💚 per uno shopping più intelligente.",
  },
  legal: legalMessagesIt,
  faq: pagesMessagesIt.faq,
  support: pagesMessagesIt.support,
  advertising: pagesMessagesIt.advertising,
} satisfies Messages;
